import { useCallback, useEffect, useState } from "react";
import { Copy, KeyRound, Loader2, ShieldCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "./AuthContext";

const card = "rounded-2xl border border-white/10 bg-[#141110] p-5";
const inputCls = "mt-1 h-10 w-full rounded-xl border border-white/10 bg-[#0C0A09] px-3 text-sm text-stone-100 outline-none focus:border-orange-500";
const when = (v) => (v ? v.slice(0, 16).replace("T", " ") : "–");

const useLoad = (url) => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get(url); setData(r.data ?? null); } catch (err) { toast.error(formatApiError(err)); }
  }, [url]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

const TempPassword = ({ value, onClose }) => (
  <div role="alert" className="rounded-2xl border border-amber-400/40 bg-amber-400/10 p-4 text-sm text-amber-100" data-testid="staff-temp-password">
    <p className="font-semibold">Temporary password (shown once)</p>
    <p className="mt-1 text-xs">Share it privately with the staff member. They must change it on first sign-in.</p>
    <div className="mt-2 flex items-center gap-2">
      <code className="rounded-lg bg-black/60 px-3 py-1.5 font-mono text-sm" data-testid="staff-temp-password-value">{value}</code>
      <button type="button" aria-label="Copy password" onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }} className="grid h-8 w-8 place-items-center rounded-lg border border-white/20"><Copy className="h-3.5 w-3.5" /></button>
      <button type="button" onClick={onClose} className="ml-auto text-xs underline" data-testid="staff-temp-password-dismiss">I've shared it</button>
    </div>
  </div>
);

export const StaffAdmin = () => {
  const { user } = useAuth();
  const [d, load] = useLoad("/admin/staff");
  const [f, setF] = useState({ email: "", name: "", role: "customer_support" });
  const [temp, setTemp] = useState("");
  const [busy, setBusy] = useState(false);
  if (!d) return <p className="mt-6 text-sm text-stone-400">Loading staff…</p>;
  const roles = Array.isArray(d.roles) ? d.roles : [];
  const act = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (r?.data?.temporary_password) setTemp(r.data.temporary_password); toast.success(ok); load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const create = (e) => { e.preventDefault(); act(() => api.post("/admin/staff", f), "Staff account created").then(() => setF({ email: "", name: "", role: "customer_support" })); };
  return (
    <div className="mt-6 space-y-4" data-testid="admin-staff">
      {temp && <TempPassword value={temp} onClose={() => setTemp("")} />}
      <form onSubmit={create} className={`${card} grid gap-3 sm:grid-cols-4`} data-testid="staff-create-form">
        <label className="text-xs text-stone-400">Full name<input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="staff-create-name" /></label>
        <label className="text-xs text-stone-400">Work email<input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="staff-create-email" /></label>
        <label className="text-xs text-stone-400">Role
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={inputCls} data-testid="staff-create-role">
            {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </label>
        <button type="submit" disabled={busy} className="tc-btn-primary mt-5 inline-flex h-10 items-center justify-center gap-2 rounded-full text-sm font-semibold" data-testid="staff-create-submit">
          <UserPlus className="h-4 w-4" /> Create account
        </button>
      </form>
      <ul className="overflow-hidden rounded-2xl border border-white/10" data-testid="staff-list">
        {(d.staff ?? []).map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-3 border-b border-white/10 px-5 py-3 text-sm" data-testid={`staff-row-${s.email}`}>
            <div className="min-w-0 flex-1">
              <p className="text-stone-100">{s.name} <span className="text-stone-400">· {s.email}</span>
                {s.bootstrap && <span className="ml-2 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] uppercase text-amber-200">Recovery account</span>}
                {!s.active && <span className="ml-2 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] uppercase text-red-200">Deactivated</span>}
                {s.must_change_password && <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase text-stone-300">Temp password</span>}
                <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] uppercase ${s.mfa_enabled ? "bg-emerald-400/15 text-emerald-200" : "bg-white/5 text-stone-400"}`} data-testid={`staff-mfa-${s.email}`}>{s.mfa_enabled ? "2-step on" : "2-step off"}</span></p>
              <p className="text-xs text-stone-500">Last sign-in {when(s.last_login_at)}</p>
            </div>
            <select value={s.role} disabled={busy || s.id === user?.id} aria-label={`Role for ${s.email}`} data-testid={`staff-role-${s.email}`}
              onChange={(e) => act(() => api.patch(`/admin/staff/${s.id}`, { role: e.target.value }), "Role updated")}
              className="h-9 rounded-full border border-white/10 bg-[#0C0A09] px-3 text-xs text-stone-200">
              {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            {!s.bootstrap && <button type="button" disabled={busy} onClick={() => window.confirm(`Reset the password for ${s.email}? Their sessions will end.`) && act(() => api.post(`/admin/staff/${s.id}/reset-password`), "Password reset")}
              className="inline-flex h-9 items-center gap-1 rounded-full border border-white/15 px-3 text-xs text-stone-300" data-testid={`staff-reset-${s.email}`}><KeyRound className="h-3.5 w-3.5" /> Reset password</button>}
            {s.mfa_enabled && s.id !== user?.id && <button type="button" disabled={busy} onClick={() => window.confirm(`Reset two-step verification for ${s.email}? They will set it up again at next sign-in.`) && act(() => api.post(`/admin/staff/${s.id}/reset-mfa`), "Two-step verification reset")}
              className="h-9 rounded-full border border-white/15 px-3 text-xs text-stone-300" data-testid={`staff-reset-mfa-${s.email}`}>Reset 2-step</button>}
            {s.id !== user?.id && <button type="button" disabled={busy} data-testid={`staff-active-${s.email}`}
              onClick={() => window.confirm(`${s.active ? "Deactivate" : "Reactivate"} ${s.email}?`) && act(() => api.patch(`/admin/staff/${s.id}`, { active: !s.active }), s.active ? "Account deactivated" : "Account reactivated")}
              className={`h-9 rounded-full px-3 text-xs ${s.active ? "border border-red-400/40 text-red-200" : "border border-emerald-400/40 text-emerald-200"}`}>{s.active ? "Deactivate" : "Reactivate"}</button>}
          </li>
        ))}
      </ul>
      <div className={card}>
        <p className="font-display text-base text-stone-100">Role permissions</p>
        <ul className="mt-2 space-y-1 text-xs text-stone-400">{roles.map((r) => <li key={r.id}><strong className="text-stone-200">{r.label}:</strong> {r.permissions.join(", ")}</li>)}</ul>
      </div>
    </div>
  );
};

export const AuditLog = () => {
  const [d] = useLoad("/admin/audit");
  const [check, setCheck] = useState(null);
  const verify = async () => { try { const r = await api.get("/admin/audit/verify"); setCheck(r.data); } catch (err) { toast.error(formatApiError(err)); } };
  if (!d) return <p className="mt-6 text-sm text-stone-400">Loading audit log…</p>;
  return (
    <div className="mt-6 space-y-3" data-testid="admin-audit">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={verify} className="inline-flex h-10 items-center gap-2 rounded-full border border-white/15 px-4 text-sm text-stone-200" data-testid="audit-verify-btn"><ShieldCheck className="h-4 w-4" /> Verify integrity</button>
        {check && <span className={`text-sm ${check.valid ? "text-emerald-300" : "text-red-300"}`} data-testid="audit-verify-result">{check.valid ? `Intact: ${check.checked} records verified` : `Tampering detected at record ${check.broken_at_seq}`}</span>}
        <span className="ml-auto text-xs text-stone-400">{d.total} records · append-only</span>
      </div>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full min-w-[640px] text-left text-xs text-stone-300">
          <thead className="text-stone-400"><tr>{["#", "Time (UTC)", "Staff", "Action", "Target", "Details"].map((h) => <th key={h} className="py-1.5 pr-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>{(d.items ?? []).map((e) => (
            <tr key={e.seq} className="border-t border-white/5 align-top" data-testid={`audit-row-${e.seq}`}>
              <td className="py-1.5 pr-3">{e.seq}</td><td className="pr-3">{when(e.at)}</td><td className="pr-3">{e.actor?.email}{e.actor?.role ? ` (${e.actor.role})` : ""}</td>
              <td className="pr-3 font-mono text-orange-200">{e.action}</td><td className="pr-3">{e.target ?? "–"}</td>
              <td className="break-all text-stone-400">{e.details ? JSON.stringify(e.details) : ""}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
};

export const Retention = () => {
  const { can } = useAuth();
  const [d, load] = useLoad("/admin/retention");
  const [f, setF] = useState(null);
  useEffect(() => { if (d?.settings) setF(d.settings); }, [d]);
  if (!d || !f) return <p className="mt-6 text-sm text-stone-400">Loading retention…</p>;
  const editable = can("retention.manage");
  const save = async () => { try { await api.put("/admin/retention", f); toast.success("Review periods saved"); load(); } catch (err) { toast.error(formatApiError(err)); } };
  const fields = [["active_enquiries_review_months", "Active enquiries"], ["archived_enquiries_review_months", "Archived enquiries"], ["audit_log_review_months", "Audit log"], ["email_records_review_months", "Email records"]];
  return (
    <div className="mt-6 space-y-4" data-testid="admin-retention">
      <p className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-100">Business enquiries are never deleted automatically. Review periods only flag records for a manual decision until a formal retention policy is approved.</p>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {(d.categories ?? []).map((c) => (
          <div key={c.id} className={card} data-testid={`retention-${c.id}`}>
            <p className="text-sm font-semibold text-stone-100">{c.label}</p>
            <p className="mt-1 text-2xl text-stone-50">{c.count}</p>
            {c.review_months != null && <p className="text-xs text-stone-400">Review after {c.review_months} months · {c.due_for_review} due</p>}
            <p className="mt-2 text-xs text-stone-400">{c.automatic_deletion}</p>
          </div>
        ))}
      </div>
      <div className={`${card} grid gap-3 sm:grid-cols-4`}>
        {fields.map(([k, l]) => (
          <label key={k} className="text-xs text-stone-400">{l}: review after (months)
            <input type="number" disabled={!editable} value={f[k]} onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })} className={inputCls} data-testid={`retention-input-${k}`} />
          </label>
        ))}
        {editable && <button type="button" onClick={save} className="tc-btn-primary h-10 rounded-full px-5 text-sm font-semibold sm:col-span-4 sm:w-fit" data-testid="retention-save">Save review periods</button>}
      </div>
    </div>
  );
};

const Section = ({ docId, s, editable, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [body, setBody] = useState(s.body);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/legal/${docId}/sections/${s.id}`, { body }); toast.success("Section saved"); setEdit(false); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <section className="border-t border-stone-200 py-6" data-testid={`legal-section-${s.id}`}>
      <h2 className="flex flex-wrap items-center gap-2 font-display text-lg text-stone-900">{s.heading}
        {s.legal_review && <span className="rounded-full bg-amber-100 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-800">Legal review</span>}</h2>
      {edit ? (
        <div className="mt-2">
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="w-full rounded-xl border border-stone-300 p-3 text-sm text-stone-900" data-testid={`legal-edit-${s.id}`} />
          <div className="mt-2 flex gap-2">
            <button type="button" disabled={busy} onClick={save} className="h-9 rounded-full bg-orange-500 px-4 text-xs font-semibold text-black" data-testid={`legal-save-${s.id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}</button>
            <button type="button" onClick={() => { setEdit(false); setBody(s.body); }} className="h-9 rounded-full border border-stone-300 px-4 text-xs text-stone-700">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          {s.body.split("\n").map((p, i) => <p key={i} className="mt-2 text-sm leading-relaxed text-stone-700">{p}</p>)}
          {editable && <button type="button" onClick={() => setEdit(true)} className="mt-2 text-xs text-orange-700 underline" data-testid={`legal-edit-btn-${s.id}`}>Edit section</button>}
        </>
      )}
    </section>
  );
};

export const LegalDrafts = () => {
  const { can } = useAuth();
  const [docId, setDocId] = useState("privacy");
  const [d, load] = useLoad(`/admin/legal/${docId}`);
  return (
    <div className="mt-6" data-testid="admin-legal">
      <div className="flex gap-2">
        {[["privacy", "Privacy Policy"], ["terms", "Terms of Use"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setDocId(k)} aria-pressed={docId === k} data-testid={`legal-tab-${k}`}
            className={`h-9 rounded-full px-4 text-xs ${docId === k ? "bg-white text-black" : "border border-white/15 text-stone-300"}`}>{l}</button>
        ))}
      </div>
      {d && d.id === docId && (
        <article className="mt-4 rounded-[24px] bg-[#F7F3EE] p-6 sm:p-10" data-testid={`legal-doc-${docId}`}>
          <p className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="legal-draft-banner">
            <strong>DRAFT: pending legal review.</strong> Not published and not in force. Visible to signed-in staff only. Version {d.version}, last edited {when(d.updated_at)} by {d.updated_by}.
          </p>
          <h1 className="mt-6 font-display text-3xl text-stone-900 sm:text-4xl">{d.title}</h1>
          {(d.sections ?? []).map((s) => <Section key={s.id} docId={docId} s={s} editable={can("legal.edit")} onSaved={load} />)}
        </article>
      )}
    </div>
  );
};
